"use client";

import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Drawer,
    Form,
    Input,
    Spin,
    message,
    Space,
    Popconfirm,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useCategoriesStore, { Category } from "./CategoriesStore.ts";

const { TextArea } = Input;

export default function Categories() {
    const {
        getCategories,
        getCategoryById,
        createCategory,
        updateCategory,
        deleteCategory,
        categoriesData,
        categoryData,
        isListLoading,
        isDetailLoading,
        isSaving,
    } = useCategoriesStore();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCategories();
    }, [currentPage, pageSize, searchText]);

    const fetchCategories = () => {
        const params: any = {
            page: currentPage,
            per_page: pageSize,
        };
        if (searchText) {
            params.query = searchText;
        }
        getCategories(params);
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleAddCategory = () => {
        setIsEditMode(false);
        form.resetFields();
        setIsDrawerOpen(true);
    };

    const handleEditCategory = async (id: number) => {
        setIsEditMode(true);
        setIsDrawerOpen(true);
        form.resetFields();
        const result = await getCategoryById(id);
        if (result.success && result.data) {
            form.setFieldsValue(result.data.data);
        } else {
            message.error("Failed to load category details");
        }
    };

    const handleDeleteCategory = async (id: number) => {
        const result = await deleteCategory(id);
        if (result.success) {
            fetchCategories();
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            let result;
            if (isEditMode && categoryData?.data?.id) {
                result = await updateCategory(categoryData.data.id, values);
            } else {
                result = await createCategory(values);
            }

            if (result.success) {
                setIsDrawerOpen(false);
                fetchCategories();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const columns: ColumnsType<Category> = [
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-folder text-black"></i>
                    <span className="font-semibold">Name</span>
                </span>
            ),
            dataIndex: "name",
            key: "name",
            render: (text) => (
                <span className="font-medium text-gray-900">{text}</span>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-tag text-black"></i>
                    <span className="font-semibold">Display Name</span>
                </span>
            ),
            dataIndex: "display_name",
            key: "display_name",
            render: (text) => (
                <span className="text-gray-700">{text}</span>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-align-left text-black"></i>
                    <span className="font-semibold">Description</span>
                </span>
            ),
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            render: (text) => (
                <span className="text-gray-600">{text || "—"}</span>
            ),
        },
        {
            title: <span className="font-semibold">Actions</span>,
            key: "action",
            align: "center",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        type="text"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditCategory(record.id)}
                    />
                    <Popconfirm
                        title="Delete this category?"
                        description="Are you sure to delete this category?"
                        onConfirm={() => handleDeleteCategory(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<i className="fa-solid fa-trash"></i>}
                            type="text"
                            danger
                            className="hover:bg-red-50"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: categoriesData?.current_page || 1,
        pageSize: categoriesData?.per_page || 10,
        total: categoriesData?.total || 0,
        showSizeChanger: true,
        showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} categories`,
    };

    return (
        <>
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>Home</span>
                    <span>/</span>
                    <span className="text-black">Categories</span>
                </div>
                <h1 className="text-3xl font-bold text-black">Categories Management</h1>
            </div>

            <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                <div className="p-6">
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-primary-600">
                                <i className="fa-solid fa-folder text-black text-lg"></i>
                            </div>
                            <h3 className="text-lg font-bold text-black m-0">All Categories</h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <Input.Search
                                placeholder="Search categories..."
                                onSearch={handleSearch}
                                style={{ width: 300 }}
                                className="rounded-xl bg-gray-50 hover:bg-gray-100 border-transparent"
                                prefix={<i className="fa-solid fa-magnifying-glass text-gray-400"></i>}
                            />
                            <Button
                                type="primary"
                                icon={<i className="fa-solid fa-plus text-black"></i>}
                                className="shadow-lg shadow-yellow-200/50"
                                onClick={handleAddCategory}
                            >
                                Add New Category
                            </Button>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={categoriesData?.data}
                        loading={isListLoading}
                        rowKey="id"
                        pagination={paginationConfig}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <span className="flex items-center gap-2 text-lg">
                        <i className="fa-solid fa-folder text-black"></i>
                        {isEditMode ? "Edit Category" : "New Category"}
                    </span>
                }
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                width={520}
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} loading={isSaving}>
                            Save
                        </Button>
                    </div>
                }
            >
                {isDetailLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin tip="Loading..." />
                    </div>
                ) : (
                    <Form form={form} layout="vertical" className="pt-4">
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true, message: "Please enter category name" }]}
                            extra="Lowercase, no spaces (e.g., restaurant, hotel, cafe)"
                        >
                            <Input placeholder="e.g., restaurant" />
                        </Form.Item>

                        <Form.Item
                            name="display_name"
                            label="Display Name"
                            rules={[{ required: true, message: "Please enter display name" }]}
                            extra="The name shown to users"
                        >
                            <Input placeholder="e.g., Nhà hàng" />
                        </Form.Item>

                        <Form.Item
                            name="description"
                            label="Description"
                        >
                            <TextArea
                                rows={4}
                                placeholder="Category description..."
                            />
                        </Form.Item>
                    </Form>
                )}
            </Drawer>
        </>
    );
}

